"use client"

import { useEffect, useState } from "react"
import Link from "next/link"
import { Button } from "@/components/ui/button"
import { ScrollArea } from "@/components/ui/scroll-area"
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet"
import { ChevronDown, ChevronRight, Menu } from "lucide-react"
import { useI18n } from "@/contexts/lang-context"
import { usePathname } from "next/navigation"

export function DocSidebar({ documentation, activeSection, activeArticle }: {
  documentation: any,
  activeSection?: string | null
  activeArticle?: string | null
}) {
  const [open, setOpen] = useState(false)
  const { locale: activeLang } = useI18n()
  const [currentSection, setCurrentSection] = useState(activeSection)
  const [currentArticle, setCurrentArticle] = useState(activeArticle)
  const [expandedSections, setExpandedSections] = useState(() => {
    // Initialize with all sections expanded
    return documentation.sections.reduce((acc: any, section: any) => {
      acc[section.id] = true
      return acc
    }, {})
  })

  const toggleSection = (sectionId: string) => {
    setExpandedSections((prev: any) => ({
      ...prev,
      [sectionId]: !prev[sectionId],
    }))
  }

  const handleArticleClick = (sectionId: string, articleId: string) => {
    setOpen(false)
  }

  const pathname = usePathname()

  useEffect(() => {
    const replaced = pathname.replace('/docs/', '').replace('/section', '').replace('/article', '')
    const parts = replaced.split('/')
    if (parts.length > 1) {
      if (parts.length === 2) {
        const [doc, section] = parts
        setCurrentSection(documentation.sections.find((s: any) => s.slug === section)?.id)
        setCurrentArticle(null)
      } else if (parts.length >= 3) {
        const [doc, section, article] = parts
        setCurrentSection(documentation.sections.find((s: any) => s.slug === section)?.id)
        setCurrentArticle(documentation.sections.find((s: any) => s.slug === section)?.articles.find((a: any) => a.slug === article)?.id)
      } else {
        const [doc] = parts
        setCurrentSection(null)
        setCurrentArticle(null)
      }
    }
  }, [pathname])

  const SidebarContent = () => (
    <div className="flex h-full flex-col w-80">
      <div className="border-b p-4">
        <Link href="/docs" className="inline-flex items-center text-sm font-medium">
          <ChevronRight className="mr-1 h-4 w-4" />
          Back to Documentation
        </Link>
      </div>
      <ScrollArea type="always" className="flex-1 p-4 max-h-[calc(100vh-12rem)] space-y-1 overflow-hidden">
        <div className="mb-1">
          <Link href={`/docs/${documentation.slug ?? documentation.id}`}>
            <Button
              variant={!currentSection && !currentArticle ? 'secondary' : 'ghost'}
              className={`flex w-full items-center justify-between p-2 text-left`}
            >
              <span className="font-medium">{documentation.title[activeLang] ?? documentation.title.en_us}</span>
            </Button>
          </Link>
        </div>
        {documentation.sections.map((section: any) => (
          <div key={section.id} className="not-last:mb-1 space-y-1">
            <Button
              variant="ghost"
              className={`flex w-full items-center justify-between p-2 text-left ${
                currentSection === section.id ? "bg-accent/50 text-accent-foreground" : ""
              }`}
              onClick={() => toggleSection(section.id)}
            >
              <span className="font-medium">{section.title[activeLang] ?? section.title.en_us}</span>
              <ChevronDown
                className={`h-4 w-4 transition-transform ${expandedSections[section.id] ? "rotate-180" : ""}`}
              />
            </Button>
            {expandedSections[section.id] && (
              <div className="ml-4 mt-1 flex flex-col gap-1">
                {section.articles.map((article: any) => (
                  <Link
                    key={article.id}
                    href={`/docs/${documentation.slug ?? documentation.id}/section/${section.slug ?? section.id}/article/${article.slug ?? article.id}`}
                  >
                    <Button
                      variant="ghost"
                      className={`w-full justify-start p-2 text-left text-sm ${
                        currentArticle === article.id ? "bg-accent text-accent-foreground" : ""
                      }`}
                      onClick={() => handleArticleClick(section.id, article.id)}
                    >
                      {article.title[activeLang] ?? article.title.en_us}
                    </Button>
                  </Link>
                ))}
              </div>
            )}
          </div>
        ))}
      </ScrollArea>
    </div>
  )

  return (
    <>
      {/* Mobile sidebar */}
      <div className="sticky top-0 z-40 flex h-16 items-center gap-2 border-b bg-background px-4 md:hidden">
        <Sheet open={open} onOpenChange={setOpen}>
          <SheetTrigger asChild>
            <Button variant="outline" size="icon">
              <Menu className="h-5 w-5" />
              <span className="sr-only">Toggle menu</span>
            </Button>
          </SheetTrigger>
          <SheetContent side="left" className="w-72 p-0">
            <SidebarContent />
          </SheetContent>
        </Sheet>
        <div className="text-sm font-medium">{documentation.title[activeLang] ?? documentation.title.en_us}</div>
      </div>

      {/* Desktop sidebar */}
      <div className="hidden border-r md:block">
        <SidebarContent />
      </div>
    </>
  )
}
